import { useNavigate } from "react-router-dom";
import { ArrowRight, Sparkles, Compass, GraduationCap, Layers, Target, Briefcase } from "lucide-react";
import { useAuth } from "../lib/AuthContext";

const HERO_IMG =
  "https://images.unsplash.com/photo-1525381846010-6463f02f61ac?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODF8MHwxfHNlYXJjaHwzfHxtaW5pbWFsaXN0JTIwYXJjaGl0ZWN0dXJlJTIwc3RhaXJjYXNlfGVufDB8fHx8MTc4MjUyNzI0OXww&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  const handleStart = () => {
    if (user) navigate("/quiz");
    else {
      // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
      const redirectUrl = window.location.origin + "/dashboard";
      window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    }
  };

  return (
    <main className="bg-[#F4F4F0]">
      {/* HERO — Split-screen Swiss */}
      <section className="border-b border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-0">
          <div className="px-6 md:px-12 py-16 lg:py-24 flex flex-col justify-center fade-up">
            <div className="overline mb-6" data-testid="hero-overline">
              [ 01 ] — Career intelligence
            </div>
            <h1 className="font-heading text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95] text-[#0A0A0A]">
              Find the work
              <br />
              you were
              <br />
              <span className="text-[#002FA7]">built for.</span>
            </h1>
            <p className="mt-8 text-lg leading-relaxed text-[#5C5C55] max-w-lg">
              Answer a short guided quiz. Get AI-matched careers paired with the
              exact formal degrees and informal courses to get you there &mdash;
              plus resume-matched job openings on LinkedIn, Naukri, Indeed &amp; Shine.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <button
                onClick={handleStart}
                data-testid="hero-start-button"
                className="btn-primary group"
              >
                Start the quiz
                <ArrowRight className="w-4 h-4 group-hover:translate-x-1 transition-transform" />
              </button>
              <a href="#how" className="btn-secondary" data-testid="hero-how-button">
                How it works
              </a>
            </div>

            <div className="mt-14 grid grid-cols-3 gap-6 max-w-md">
              <div>
                <div className="font-heading font-black text-3xl text-[#0A0A0A]">5</div>
                <div className="overline mt-1">Career matches</div>
              </div>
              <div>
                <div className="font-heading font-black text-3xl text-[#0A0A0A]">8+</div>
                <div className="overline mt-1">Courses each</div>
              </div>
              <div>
                <div className="font-heading font-black text-3xl text-[#0A0A0A]">∞</div>
                <div className="overline mt-1">Possibilities</div>
              </div>
            </div>
          </div>

          <div className="relative lg:border-l border-[#E5E5E0] min-h-[420px] lg:min-h-[640px]">
            <img
              src={HERO_IMG}
              alt="A minimalist concrete staircase symbolising upward career progression"
              className="absolute inset-0 w-full h-full object-cover"
              data-testid="hero-image"
            />
            <div className="absolute top-6 left-6 bg-[#F4F4F0] px-3 py-1.5 border border-[#0A0A0A]">
              <span className="overline">Path • Progress • Purpose</span>
            </div>
            <div className="absolute bottom-6 right-6 bg-[#002FA7] text-white px-4 py-3 max-w-[220px]">
              <div className="font-mono text-xs uppercase tracking-widest opacity-70">Now</div>
              <div className="font-heading font-bold text-lg leading-tight mt-1">
                Powered by Claude Sonnet 4.5
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section id="how" className="border-b border-[#E5E5E0] grid-lines">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
            <div>
              <div className="overline mb-3">[ 02 ] — Method</div>
              <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-none text-[#0A0A0A]">
                Three deliberate steps.
              </h2>
            </div>
            <p className="text-[#5C5C55] max-w-md text-base leading-relaxed">
              No spinning wheels. No vague horoscopes. A focused process to
              translate what you love into what you can do — and learn.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-px bg-[#E5E5E0] border border-[#E5E5E0]">
            {[
              {
                n: "01",
                title: "Tell us about you",
                body: "A 6-step quiz on interests, skills, and goals. Five minutes.",
                Icon: Sparkles,
              },
              {
                n: "02",
                title: "See your matches",
                body: "Five AI-curated careers with match score, salary range, and growth outlook. Add the ₹99 PDF to keep everything shareable.",
                Icon: Compass,
              },
              {
                n: "03",
                title: "Learn & apply",
                body: "Formal degrees, informal courses, videos & books. Upload your resume for ₹101 to get real openings on LinkedIn, Naukri, Indeed & Shine.",
                Icon: GraduationCap,
              },
            ].map((s) => (
              <div
                key={s.n}
                className="bg-[#FFFFFF] p-8 md:p-10 hover:bg-[#FAFAF7] transition-colors"
                data-testid={`how-step-${s.n}`}
              >
                <div className="flex items-center justify-between mb-8">
                  <span className="font-mono text-sm text-[#002FA7]">{s.n}</span>
                  <s.Icon className="w-5 h-5 text-[#0A0A0A]" strokeWidth={1.5} />
                </div>
                <h3 className="font-heading text-2xl font-bold tracking-tight mb-3">
                  {s.title}
                </h3>
                <p className="text-[#5C5C55] leading-relaxed">{s.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FEATURES */}
      <section className="border-b border-[#E5E5E0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-5">
            <div className="overline mb-3">[ 03 ] — What you get</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tight leading-tight">
              Not just careers.
              <br />
              <span className="text-[#FF2A00]">A whole roadmap.</span>
            </h2>
          </div>
          <div className="lg:col-span-7 grid sm:grid-cols-2 gap-5">
            {[
              { Icon: Target, title: "Match score per career", body: "Know exactly how strong a fit each path is." },
              { Icon: Layers, title: "Formal + informal mix", body: "Degrees, certs, and free YouTube — one plan." },
              { Icon: GraduationCap, title: "Course tracker", body: "Track progress through every recommended course." },
              { Icon: Compass, title: "Save & revisit", body: "Bookmark careers, come back when you're ready." },
              { Icon: Sparkles, title: "Shareable PDF report", body: "10-12 pages, all 5 careers, resources & tracker. ₹99." },
              { Icon: Target, title: "Job matches on 4 platforms", body: "Upload resume, get openings on LinkedIn, Naukri, Indeed & Shine. ₹101." },
            ].map((f, i) => (
              <div key={i} className="border border-[#E5E5E0] bg-white p-6 hover:border-[#002FA7]/40 transition-colors">
                <f.Icon className="w-5 h-5 text-[#002FA7] mb-4" strokeWidth={1.5} />
                <h4 className="font-heading text-lg font-bold mb-1.5">{f.title}</h4>
                <p className="text-sm text-[#5C5C55] leading-relaxed">{f.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* JOB MATCH PROMO */}
      <section className="border-b border-[#E5E5E0] bg-[#F4F4F0]">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-16">
          <div
            data-testid="landing-jobs-banner"
            className="border border-[#4C1D95] bg-[#5B21B6] text-white rounded-sm p-6 md:p-8 flex flex-col md:flex-row md:items-center gap-6 md:gap-8"
          >
            <div className="w-12 h-12 bg-[#FACC15] flex items-center justify-center shrink-0">
              <Briefcase className="w-6 h-6 text-[#0A0A0A]" strokeWidth={1.8} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-widest text-white/70 mb-1">
                NEW &middot; Job Match Report &middot; For college & working professionals
              </div>
              <h3 className="font-heading text-2xl md:text-3xl font-black leading-tight tracking-tight">
                Upload your resume &mdash; get real openings on
                <span className="text-[#FACC15]"> LinkedIn, Naukri, Indeed &amp; Shine</span>
              </h3>
              <p className="text-sm text-white/80 mt-2 leading-relaxed max-w-2xl">
                AI compares your resume to your chosen career, surfaces your strengths &amp; skill gaps,
                and hands you 4 pre-filled job searches tuned to your profile.
              </p>
            </div>
            <div className="flex items-center gap-4 shrink-0">
              <div className="bg-[#FACC15] text-[#0A0A0A] px-5 py-3 rounded-sm font-heading font-bold text-xl leading-none flex flex-col items-center">
                <span className="font-mono text-[9px] tracking-widest text-[#0A0A0A]/70 mb-1">STARTS AT</span>
                <span>&#8377;101</span>
              </div>
              <button
                onClick={handleStart}
                data-testid="landing-jobs-cta"
                className="bg-white text-[#5B21B6] px-5 py-3 font-semibold rounded-sm hover:-translate-y-0.5 transition-transform inline-flex items-center gap-2 whitespace-nowrap"
              >
                Take the quiz <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-[#002FA7] text-white">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 flex flex-col md:flex-row items-start md:items-center justify-between gap-8">
          <div>
            <div className="overline !text-white/70 mb-4">[ 04 ] — Begin</div>
            <h2 className="font-heading text-4xl sm:text-5xl font-black tracking-tighter leading-none">
              Your next chapter,
              <br />
              one quiz away.
            </h2>
          </div>
          <button
            onClick={handleStart}
            data-testid="cta-start-button"
            className="bg-white text-[#002FA7] px-7 py-4 font-semibold rounded-sm hover:-translate-y-0.5 transition-transform inline-flex items-center gap-2"
          >
            Take the quiz <ArrowRight className="w-4 h-4" />
          </button>
        </div>
      </section>

      <footer className="border-t border-[#E5E5E0] py-8 text-center">
        <p className="overline">© 2026 — CareerWisdom</p>
      </footer>
    </main>
  );
}
